import { useEffect, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, FormControlLabel, TextField, makeStyles, Box, Fab, Switch, Typography } from "@material-ui/core";
import { Autocomplete, createFilterOptions } from '@material-ui/lab';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import SaveIcon from '@material-ui/icons/Save';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Http from '../../engine/Http';
import { Dimension, Price, getDimensionsDisplayText } from '../../model/Items';
import { convertNumberToDisplayedNumberText } from '../../model/Numbers';
import useAuthenticationHeader from '../../security/AuthenticationHeader.hook';

interface KitElementsExpansionPanelProps {
  readonly customerId: number;
  readonly priceListId: number;
  kitId: number|null,
  elementName: string;
  title: string,
  promptMessage: string;
  onElementsChanged: () => void
};

interface Material {
  id: number;
  name: string;
};

interface ElementItem
extends Element {
  dimension: Dimension;
  price: Price;
};

interface DisplayedElement
extends Element {
  dimension?: Dimension;
  price?: Price;
};

interface Element {
  id: number;
  name: string;
  material: Material;
};

interface KitElement {
  element: Element;
  quantity: number;
};

const useAccordionStyles = makeStyles(theme => ({
  root: {
    marginTop: 10
  },
}));

const useKitElementStyles = makeStyles(theme => ({
  root: {
    marginRight: 0
  },
  label: {
    width: 250,
    margin: theme.spacing(1)
  },
}));

const useStyles = makeStyles(theme => ({
  elementSelect: {
    width: 250,
    margin: theme.spacing(2)
  },
  quantityInput: {
    width: 100
  },
  kitElementButton: {
    marginTop: theme.spacing(1),
    marginLeft: theme.spacing(1)
  },
  filterOnlyDimemsionsSwitch: {
    paddingBottom: theme.spacing(2),
    paddingLeft: theme.spacing(8),
    marginTop: theme.spacing(-0.5),
  }
}));

const convertToElementItem = (elementData: any) : ElementItem => ({
  ...convertToElement(elementData),
  dimension: elementData.dimension || elementData.block,
  price: elementData.price || elementData.unitPrice
});

const convertToElement = (elementData: any) : Element => ({
  id: elementData.id,
  name: elementData.name,
  material: elementData.material
});

const getKitElementIds = (kitElements: KitElement[]) : number[] => kitElements.map(kitElement => kitElement.element.id);

const KitContentsAccordion = (props: KitElementsExpansionPanelProps) => {
  const classes = useStyles();
  const kitElementClasses = useKitElementStyles();
  const accordionElementClasses = useAccordionStyles();
  const authenticationHeader = useAuthenticationHeader();
  const customerId = props.customerId;
  const priceListId = props.priceListId;
  const kitElementDefinitionsPath =   `/api/customers/${customerId}/price-lists/${priceListId}/${props.elementName}s`
  const kitElementsPath = `/api/customers/${customerId}/price-lists/${priceListId}/kits/${props.kitId}/${props.elementName}s`;
  const [selectedElement, setSelectedElement] = useState<ElementItem|null>(null);
  const [selectedQuantity, setSelectedQuantity] = useState<number>(0);
  const [referenceElements, setReferenceElements] = useState<ElementItem[]>([]);
  const [selectableElements, setSelectableElements] = useState<ElementItem[]>([]);
  const [kitElements, setKitElements] = useState<KitElement[]>([]);
  const [fetchedKitElements, setFetchedKitElements] = useState<KitElement[]>([]);
  const [expanded, setExpanded] = useState<boolean|null>(null);
  const kitElementCountDetails = kitElements ? ` (${kitElements.length})` : '';
  const [filterOnlyDimensions, setFilterOnlyDimensions] = useState<boolean>(false);

  const handleAccordionChanged = (expanded: boolean) => {
    setExpanded(expanded);
  };

  const handleSelectedElementChange = (event: object, selectedElement: any) => {
    setSelectedElement(selectedElement);
  };

  const convertToKitElement = (kitElementData: any) => ({
    element: convertToElement(kitElementData[props.elementName]),
    quantity: kitElementData.quantity
  });

  const fetchSelectableElements = () => Http
    .get(kitElementDefinitionsPath, [ authenticationHeader ])
    .then(data => {
      console.debug(`Elements data fetched: ${JSON.stringify(data)}`);
      const referenceElements = (data as []).map(convertToElementItem);
      setReferenceElements(referenceElements);
      setSelectableElements(referenceElements.filter(
        element => !getKitElementIds(kitElements).includes(element.id)));
    })
    .catch(reason => console.error(`Could not fetch elements data: ${reason}`));

  const fetchKitElements = () => Http
    .get(kitElementsPath, [ authenticationHeader ])
    .then(data => {
      console.debug(`Kit elements data fetched: ${JSON.stringify(data)}`);
      const kitElements: KitElement[] = (data as any[]).map(convertToKitElement);
      setFetchedKitElements(kitElements);
      const editableKitElements = kitElements
        .sort((kitElement, anotherKitElement) => kitElement.element.name.localeCompare(anotherKitElement.element.name))
        .map(kitElement => ({ element: kitElement.element, quantity: kitElement.quantity }));
      setKitElements(editableKitElements);
    })
    .catch(reason => console.error(`Could not fetch kit elements data: ${reason}`));

  const findKitElement = (kitElements: KitElement[], elementId: number) =>
    kitElements.find(kitElement => kitElement.element.id === elementId);

  const handleKitElementChange = (elementId: number, quantity: string) =>
    setKitElements(kitElements.map(kitElement => kitElement.element.id === elementId
      ? { ...kitElement, quantity: Number(quantity) }
      : kitElement));

  const handleSelectedQuantityChange = (quantity: string) => setSelectedQuantity(Number(quantity));

  const clearSelectedValues = () => {
    setSelectedElement(null);
    setSelectedQuantity(0);
  };

  const addKitElement = (kitElement: any) => Http
    .post(kitElementsPath, kitElement, [ authenticationHeader ])
    .then(fetchKitElements)
    .then(clearSelectedValues)
    .then(props.onElementsChanged)
    .catch(reason => console.error(`Could not add kit element: ${reason}`));

  const getSelectedKitElement = () =>
    (selectedElement && selectedQuantity)
    ? ({
      [props.elementName]: selectedElement,
      quantity: selectedQuantity
    })
    : null;

  const handleAddKitElement = () => {
    const kitElement = getSelectedKitElement(); 
    if (kitElement) {
      console.debug(`Adding new kit element: ${JSON.stringify(kitElement)}`);
      addKitElement(kitElement);
    } else {
      console.debug('No element selected');
    }
  };

  const handleUpdateKitElement = (kitElement: KitElement) => Http
    .put(`${kitElementsPath}/${kitElement.element.id}`, { quantity: kitElement.quantity }, [ authenticationHeader ])
    .then(fetchKitElements)
    .then(clearSelectedValues)
    .then(props.onElementsChanged)
    .catch(reason => console.error(`Could not update kit element: ${reason}`));

  const handleDeleteKitElement = (deletedKitElement: KitElement) => Http
    .delete(`${kitElementsPath}/${deletedKitElement.element.id}`, [ authenticationHeader ])
    .then(fetchKitElements)
    .then(clearSelectedValues)
    .then(props.onElementsChanged)
    .catch(reason => console.error(`Could not delete kit element: ${reason}`));

  const isAddKitElementDisabled = () => !selectedElement || selectedQuantity <= 0;

  const isUpdateKitElementDisabled = (kitElement: KitElement) => kitElement.quantity <= 0
    || findKitElement(fetchedKitElements, kitElement.element.id)?.quantity === kitElement.quantity;

  const getElementOptionDisplayedText = (element: ElementItem) : string => getElementDisplayedText(element);

  const findReferenceElementItem = (id: number) : ElementItem|undefined => referenceElements.find(element => element.id == id);

  const getKitComponentElementDisplayedText = (element: Element) => {
    const elementItem = findReferenceElementItem(element.id);
    return getElementDisplayedText({
      ...element,
      dimension: elementItem?.dimension,
      price: elementItem?.price
    });
  };

  const getDisplayedPriceAdjustment = (price: Price) => {
    const priceAdjustment = price.adjustment;
    return priceAdjustment === undefined ? 0 : convertNumberToDisplayedNumberText(priceAdjustment);
  };

  const getElementDisplayedText = (element: DisplayedElement) => {
    const dimension = element?.dimension;
    const price = element?.price;
    const displayedDimension = dimension === undefined ? '?' : getDimensionsDisplayText(dimension);
    const displayedPriceAdjustment = price === undefined ? '?' : `+${getDisplayedPriceAdjustment(price)}%`;
    return `${element.name} (${element.material.name}) [${displayedDimension}] <${displayedPriceAdjustment}>`;
  };

  const renderKitElement = (kitElement: KitElement) => {
    const element = kitElement.element;
    const quantity = findKitElement(kitElements, element.id)?.quantity;
    return (
      <Box display='flex' flexDirection='row'>
        <FormControlLabel
          classes={ kitElementClasses }
          label={ getKitComponentElementDisplayedText(element) }
          control={
            <TextField
              type='number'
              variant='outlined'
              className={ classes.quantityInput }
              onChange={ event => handleKitElementChange(element.id, event.target.value) }
              value={ quantity }
            />
          }
          labelPlacement='start'
        />
        <Fab
          className={classes.kitElementButton}
          aria-label='update'
          color='primary'
          onClick={() => handleUpdateKitElement(kitElement)}
          size='small'
          disabled={isUpdateKitElementDisabled(kitElement)}
        >
          <SaveIcon />
        </Fab>
        <Fab
          className={classes.kitElementButton}
          aria-label='delete'
          color='secondary'
          onClick={() => handleDeleteKitElement(kitElement)}
          size='small'
        >
          <DeleteIcon />
        </Fab>
      </Box>
    );
  };

  const renderKitElementSelectionBox = () => {
    const filterOptions = createFilterOptions({
      stringify: (option: ElementItem) : string => {
        const dimensions = option.dimension;
        return dimensions === undefined ? '' : getDimensionsDisplayText(dimensions);
      }
    });
    return (
      <Box display='flex' flexDirection='row'>
        <Autocomplete
            value={ selectedElement }
            onChange={ handleSelectedElementChange }
            className={ classes.elementSelect }
            options={ selectableElements }
            getOptionLabel={ getElementOptionDisplayedText }
            renderInput={ (params) => <TextField {...params} /> }
            filterOptions={ filterOnlyDimensions ? filterOptions : undefined }
        />
        <TextField
          type='number'
          variant='outlined'
          className={classes.quantityInput}
          onChange={event => handleSelectedQuantityChange(event.target.value)}
          value={selectedQuantity}
        />
        <Fab
          className={classes.kitElementButton}
          aria-label='add'
          color='primary'
          onClick={handleAddKitElement}
          size='small'
          disabled={isAddKitElementDisabled()}
        >
          <AddIcon />
        </Fab>
      </Box>
    );
  };

  const renderKitElements = () => kitElements.map(renderKitElement);

  const handleOnlyDimensionsChanged
    = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => setFilterOnlyDimensions(checked);

  const renderKitElementsSelectionCaption = () : JSX.Element =>
    <Box display='flex' flexDirection='row'>
      { props.promptMessage }
      <FormControlLabel
        className={ classes.filterOnlyDimemsionsSwitch }
        label="Filtruj tylko według wymiarów"
        control={
          <Switch
            color='primary'
            defaultChecked
            checked={ filterOnlyDimensions }
            onChange={ handleOnlyDimensionsChanged }
            size='small'
          />}
      />
    </Box>
  ;

  useEffect(() => {
    if (expanded === null || expanded === true) {
      if (props.kitId) {
        fetchKitElements();
      } 
    }
  }, [expanded]);

  useEffect(() => {
    fetchSelectableElements();
  }, [kitElements]);

  return (
    <Accordion classes={accordionElementClasses} onChange={(event, expanded) => handleAccordionChanged(expanded)}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        { props.title + kitElementCountDetails }
      </AccordionSummary>
      <AccordionDetails>
        <Box display='flex' flexDirection='column' padding={0} margin={0}>
          { selectableElements.length > 0 && renderKitElementsSelectionCaption() }
          { selectableElements.length > 0 && renderKitElementSelectionBox() }
          { renderKitElements() }
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
export default KitContentsAccordion;